import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageLayout } from 'src/layouts';
import type { IssueFormDataFields } from 'src/pages/issues/update/forms/issueSchema';
import { Permission } from 'src/rbac/Permission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useInsertChildIssue } from '@/hooks/mutations';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetIssueRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';
import { createWithFiles } from '@/utils/createWithFiles';
import { IssueTypeMapping } from '@/utils/issueVariantUtils';

import { useGetRegisterTableProps } from './config';
import IssueModal from './IssueModal';
import { IssueRegisterToolbar } from './IssueRegisterToolbar';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';
type Props = {
  issueType: ParentIssueType;
};

const Page: FC<Props> = ({ issueType }) => {
  const { t } = useTranslation(['common'], {});
  const issueTypeMap = IssueTypeMapping[issueType];
  const { t: st } = useTranslation(['common'], {
    keyPrefix: issueTypeMap.taxonomy,
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { updateFiles } = useFileUpdate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const openCreate = useCallback(() => setIsModalVisible(true), []);
  const { data, loading } = useGetIssueRegister({
    queryArgs: { issueType },
  });

  const tableProps = useGetRegisterTableProps(issueType, data?.issue, loading);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters(t(issueTypeMap.entityLabelOther))
  );

  const counter = getCounter(tableProps.totalItemsCount, loading);
  const { insertChildIssue } = useInsertChildIssue();

  const onSave = async (data: IssueFormDataFields) => {
    const { files } = data;

    const { createdEntity } = await createWithFiles({
      create: () =>
        insertChildIssue({
          Type: issueType,
          CustomAttributeData: data.CustomAttributeData || undefined,
          TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
          DepartmentTypeIds:
            data.departments?.map((d) => d.DepartmentTypeId) || [],
          DateIdentified: data.DateIdentified,
          DateOccurred: data.DateOccurred,
          Details: data.Details,
          Title: data.Title,
          ...ownerContributorAndViewerIds(data),
          IsExternalIssue: data.IsExternalIssue,
          ImpactsCustomer: data.ImpactsCustomer,
        }),
      getCreatedEntity: (result) => {
        const issueId = result.insertChildIssue?.Id;
        if (!issueId) {
          throw new Error('Failed to insert issue');
        }

        return { id: issueId, destination: issueTypeMap.detailsUrl(issueId) };
      },
      saveFiles: ({ id }) =>
        updateFiles({
          parentType: Parent_Type_Enum.Issue,
          parentId: id,
          originalFiles: [], // This is always an insert so no original files
          selectedFiles: files,
        }),
    });

    return { savedIds: [createdEntity.id], createdEntity };
  };

  const title = st('register_title');

  return (
    <PageLayout
      helpTranslationKey={'issues.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-156) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <IssueRegisterToolbar
            title={title}
            counter={counter}
            issueType={issueType}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            onCreate={openCreate}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <Permission permission={'insert:issue'}>
            <Button variant={'primary'} onClick={openCreate}>
              {st('create_new_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={issueType}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
      {isModalVisible && (
        <IssueModal
          onDismiss={() => setIsModalVisible(false)}
          onSaving={onSave}
          issueType={issueType}
        />
      )}
    </PageLayout>
  );
};

export default Page;
